import { createEffect, createSignal, For, onMount, type JSX } from "solid-js";

interface Props {
    /** Numero de digitos. Default 6 (TOTP padrao RFC 6238). */
    length?: number;
    /** String controlada (0 a `length` chars). Pode ter chars vazios no meio. */
    value: string;
    /** Chamado sempre que o valor muda. */
    onInput: (v: string) => void;
    /**
     * Chamado quando todos os digitos sao preenchidos. Use pra auto-submit.
     * Recebe a string completa.
     */
    onComplete?: (v: string) => void;
    /** Desabilita todos os boxes (ex: durante request). */
    disabled?: boolean;
    /**
     * Marca como invalido: shake + borda vermelha + limpa value + foca no
     * primeiro box (com 600ms de delay pro shake aparecer). Toggle de false
     * pra true dispara o efeito uma vez.
     */
    invalid?: boolean;
    /** aria-label do grupo (ex: "Codigo de verificacao"). */
    label: string;
    /** aria-describedby (ex: id de paragrafo explicativo). */
    describedBy?: string;
    /** Foca no primeiro box ao montar. Default true. */
    autoFocus?: boolean;
}

/**
 * Input OTP por digito acessivel.
 *
 * Comportamento de teclado:
 * - Digitar avanca pro proximo box automaticamente.
 * - Backspace em box vazio volta + limpa o anterior.
 * - Backspace em box preenchido limpa o atual sem mover foco.
 * - Setas esq/dir navegam entre boxes.
 * - Paste (Ctrl/Cmd+V) com 6 digitos preenche todos.
 *
 * Acessibilidade:
 * - role=group + aria-label no container.
 * - Cada input tem aria-label "Digito N de M" + aria-invalid quando erro.
 * - autocomplete="one-time-code" no primeiro input habilita autofill iOS SMS.
 * - shake animation respeita prefers-reduced-motion.
 */
export function OtpInput(props: Props): JSX.Element {
    const length = () => props.length ?? 6;
    const inputs: HTMLInputElement[] = [];

    onMount(() => {
        if (props.autoFocus !== false) {
            queueMicrotask(() => inputs[0]?.focus());
        }
    });

    // Quando invalid passa pra true, agenda limpeza apos a shake animation.
    let lastInvalid = false;
    createEffect(() => {
        const inv = !!props.invalid;
        if (inv && !lastInvalid) {
            setTimeout(() => {
                props.onInput("");
                inputs[0]?.focus();
            }, 600);
        }
        lastInvalid = inv;
    });

    function digitAt(i: number): string {
        return props.value[i] ?? "";
    }

    function commit(merged: string) {
        const trimmed = merged.slice(0, length());
        props.onInput(trimmed);
        if (trimmed.length === length() && /^[0-9]+$/.test(trimmed)) {
            props.onComplete?.(trimmed);
        }
    }

    function setAt(i: number, digit: string) {
        const chars = props.value.split("");
        while (chars.length < length()) chars.push("");
        chars[i] = digit;
        commit(chars.join(""));
    }

    function handleInput(e: InputEvent, i: number) {
        const target = e.currentTarget as HTMLInputElement;
        const raw = target.value;

        // Paste de varios digitos de uma vez (autofill OTP ou Ctrl+V).
        if (raw.length > 1) {
            const digits = raw.replace(/\D/g, "");
            if (digits.length === 0) {
                target.value = digitAt(i);
                return;
            }
            const chars = props.value.split("");
            while (chars.length < length()) chars.push("");
            const start = digits.length >= length() ? 0 : i;
            for (let j = 0; j < digits.length && start + j < length(); j++) {
                chars[start + j] = digits[j];
            }
            commit(chars.join(""));
            const lastIdx = Math.min(start + digits.length - 1, length() - 1);
            inputs[lastIdx]?.focus();
            return;
        }

        // Digito unico.
        const digit = raw.replace(/\D/g, "").slice(-1);
        target.value = digit;
        setAt(i, digit);
        if (digit && i < length() - 1) {
            inputs[i + 1]?.focus();
        }
    }

    function handleKeyDown(e: KeyboardEvent, i: number) {
        const target = e.currentTarget as HTMLInputElement;
        if (e.key === "Backspace") {
            if (target.value) {
                setAt(i, "");
                target.value = "";
                e.preventDefault();
            } else if (i > 0) {
                inputs[i - 1]?.focus();
                setAt(i - 1, "");
                if (inputs[i - 1]) inputs[i - 1].value = "";
                e.preventDefault();
            }
        } else if (e.key === "ArrowLeft" && i > 0) {
            e.preventDefault();
            inputs[i - 1]?.focus();
        } else if (e.key === "ArrowRight" && i < length() - 1) {
            e.preventDefault();
            inputs[i + 1]?.focus();
        } else if (e.key === "Home") {
            e.preventDefault();
            inputs[0]?.focus();
        } else if (e.key === "End") {
            e.preventDefault();
            inputs[length() - 1]?.focus();
        }
    }

    function handleFocus(e: FocusEvent) {
        const target = e.currentTarget as HTMLInputElement;
        // Selecionar tudo facilita sobrescrever digito.
        queueMicrotask(() => target.select?.());
    }

    return (
        <div
            class={`ui-otp ${props.invalid ? "is-invalid" : ""}`}
            role="group"
            aria-label={props.label}
            aria-describedby={props.describedBy}
        >
            <For each={Array.from({ length: length() })}>
                {(_, i) => (
                    <input
                        ref={(el) => {
                            inputs[i()] = el;
                        }}
                        type="text"
                        inputmode="numeric"
                        autocomplete={i() === 0 ? "one-time-code" : "off"}
                        maxlength={length()}
                        pattern="[0-9]*"
                        class="ui-otp-input"
                        value={digitAt(i())}
                        disabled={props.disabled}
                        aria-label={`Dígito ${i() + 1} de ${length()}`}
                        aria-invalid={props.invalid}
                        onInput={(e) => handleInput(e, i())}
                        onKeyDown={(e) => handleKeyDown(e, i())}
                        onFocus={handleFocus}
                    />
                )}
            </For>
        </div>
    );
}
